import { api, setAuthToken, getAuthToken, getSessionExpiresAt } from './client';
import { useAuthStore, type AuthProfile } from '@/stores/useAuthStore';

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  timestamp: string;
}

export interface AuthSession {
  sessionToken: string;
  expiresIn: number;
  userId?: string;
  keycloakId?: string;
  candidateProfileId?: string;
  employerProfileId?: string;
  session: {
    sessionId: string;
    userId: string;
    currentTenant: { id: string; name?: string };
    availableTenants: { id: string; name?: string }[];
    expiresAt: string;
    roles: string[];
    name?: string;
  };
}

export interface RegisterInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: 'candidate' | 'employer' | 'both';
  nationalId?: {
    type: 'NationalId' | 'Passport' | 'TaxId';
    number: string;
    country: string;
  };
  profile?: {
    profileType: 'Individual' | 'Organisation';
    headline: string;
    availability: 'Immediate' | 'TwoWeeks' | 'OneMonth' | 'Contract' | 'PerNegotiation';
    workType: 'Permanent' | 'Contract' | 'PartTime' | 'Freelance' | 'Any';
    skills: string[];
    desiredLocations: Array<{
      label: string;
      plusCode?: string;
      plusCodeShort: string;
      h3Index: string;
      h3Resolution: number;
      geocodePrecision: 'Public' | 'Private' | 'Premium';
      radiusKm: number;
    }>;
  };
  /**
   * Company payload — required for `employer`/`both`, forbidden for
   * `candidate` (payload matrix). Never fabricated client-side.
   *
   * @see Documentation/Specification/04_Processes/application-specific/workseeking/CandidateAndEmployerRegistration.md §5
   */
  employerProfile?: {
    companyName: string;
    industry?: string;
    companySize?: string;
  };
}

export interface LoginInput {
  email: string;
  password: string;
}

function mapRole(roles: string[]): AuthProfile['role'] {
  const hasCandidate = roles.some((r) => r.toLowerCase() === 'candidate');
  const hasEmployer = roles.some((r) => r.toLowerCase() === 'employer');
  if (hasCandidate && hasEmployer) return 'both';
  if (hasEmployer) return 'employer';
  return 'candidate';
}

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleRefresh(expiresAt: string): void {
  clearRefreshTimer();
  const expiryMs = new Date(expiresAt).getTime();
  const now = Date.now();
  // Refresh at 75% of TTL (e.g. 45 min into a 60 min session)
  const refreshAt = expiryMs - (expiryMs - now) * 0.25;
  const delay = Math.max(refreshAt - now, 5000); // at least 5s from now

  refreshTimer = setTimeout(async () => {
    try {
      await refreshSession();
    } catch {
      // refresh failed — session will expire, user must re-login
    }
  }, delay);
}

function clearRefreshTimer(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

export function applySession(
  resp: AuthSession,
  identity: { email: string; name: string },
): void {
  setAuthToken(resp.sessionToken, resp.session.expiresAt);
  const roles = resp.session.roles ?? [];
  useAuthStore.getState().login(
    {
      id: resp.session.userId,
      email: identity.email,
      name: identity.name,
      role: mapRole(roles),
    },
    roles,
  );
  scheduleRefresh(resp.session.expiresAt);
}

export function restoreSession(): boolean {
  const token = getAuthToken();
  const expiresAt = getSessionExpiresAt();
  if (!token || !expiresAt) return false;

  // Session expired server-side — can't restore
  if (new Date(expiresAt) < new Date()) {
    clearSession();
    return false;
  }

  // Token exists and not expired — schedule refresh
  scheduleRefresh(expiresAt);
  return true;
}

export async function refreshSession(): Promise<void> {
  const token = getAuthToken();
  if (!token) return;

  const resp = await api.post<ApiEnvelope<{ expiresAt: string }>>('/auth/session/refresh', {
    sessionToken: token,
  });

  const newExpiresAt = resp.data.expiresAt;
  setAuthToken(token, newExpiresAt);
  scheduleRefresh(newExpiresAt);
}

export function clearSession(): void {
  clearRefreshTimer();
  setAuthToken(null);
  useAuthStore.getState().logout();
}

export async function registerAccount(input: RegisterInput): Promise<AuthSession> {
  const env = await api.post<ApiEnvelope<AuthSession>>('/auth/register', input);
  return env.data;
}

export async function loginAccount(input: LoginInput): Promise<AuthSession> {
  const env = await api.post<ApiEnvelope<AuthSession>>('/auth/login', input);
  return env.data;
}

export async function requestPasswordReset(email: string): Promise<void> {
  return api.post('/auth/forgot-password', { email });
}

export async function resendVerificationEmail(email: string): Promise<void> {
  return api.post('/auth/verify-email/resend', { email });
}

export async function signOut(): Promise<void> {
  const token = getAuthToken();
  try {
    if (token) await api.delete('/auth/session');
  } catch {
    // ignore — local clear still happens
  } finally {
    clearSession();
  }
}
